import React, { useEffect, useState } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CircularProgress,
  Divider,
  Typography,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import apiClient from '../../../services/apiClient';

interface HealthResponse {
  status: string;
  environment: string;
  build_time: string;
  git_sha: string;
}

const FRONTEND_GIT_SHA = process.env.REACT_APP_GIT_SHA || 'unknown';
const FRONTEND_BUILD_TIME = process.env.REACT_APP_BUILD_TIME || 'unknown';

const InfoRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Box>
    <Typography variant="caption" color="text.secondary">{label}</Typography>
    <Typography variant="body2" fontWeight={600} fontFamily="monospace">{value}</Typography>
  </Box>
);

const SystemInfo: React.FC = () => {
  const [health, setHealth] = useState<HealthResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    apiClient.get<HealthResponse>('/health')
      .then((r) => setHealth(r.data))
      .catch((e) => setError(e.message))
      .finally(() => setLoading(false));
  }, []);

  return (
    <Box sx={{ p: 3, maxWidth: 500 }}>
      <Typography variant="h5" fontWeight={700} gutterBottom>System Info</Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Deployment details.
      </Typography>

      {loading && <CircularProgress size={24} />}
      {error && <Alert severity="error">{error}</Alert>}

      {health && (
        <Card variant="outlined" sx={{ mb: 2 }}>
          <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <CheckCircleIcon color="success" fontSize="small" />
              <Typography variant="subtitle2" fontWeight={700} color="success.main">
                Backend
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ ml: 'auto' }}>
                {health.environment}
              </Typography>
            </Box>
            <Divider />
            <InfoRow label="Last Deploy" value={health.build_time} />
            <InfoRow label="Git SHA" value={health.git_sha} />
          </CardContent>
        </Card>
      )}

      <Card variant="outlined">
        <CardContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <CheckCircleIcon color="success" fontSize="small" />
            <Typography variant="subtitle2" fontWeight={700} color="success.main">
              Frontend
            </Typography>
          </Box>
          <Divider />
          <InfoRow label="Last Deploy" value={FRONTEND_BUILD_TIME} />
          <InfoRow label="Git SHA" value={FRONTEND_GIT_SHA} />
        </CardContent>
      </Card>
    </Box>
  );
};

export default SystemInfo;
